import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem('cbm_chat_session');
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('cbm_chat_session', id);
  }
  return id;
};

const WELCOME = "Ciao! 🌺 Sono l'assistente di Caribbean Blue Moon. Posso aiutarti con corsi, lezioni di prova gratuite, shop e affitto sala. Come posso aiutarti?";

const renderText = (text) =>
  text.split('**').map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: WELCOME }]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', text: 'Ops! Problema di connessione. Riprova oppure chiamaci al 380 159 9564.' };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 md:right-6 z-[110] w-[calc(100vw-2rem)] max-w-[380px] h-[520px] max-h-[70vh] bg-white rounded-3xl shadow-2xl shadow-slate-400/30 border border-slate-100 flex flex-col overflow-hidden"
        >
          <div className="bg-[#0288D1] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles size={18} className="text-[#FFCA28]" />
              </div>
              <div>
                <p className="font-heading font-bold text-white text-sm leading-tight">Assistente Blue Moon</p>
                <p className="text-blue-100 text-xs">Ti rispondo subito!</p>
              </div>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
              <X size={18} className="text-white" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#FF7043] text-white rounded-2xl rounded-br-md'
                      : 'bg-white text-slate-700 rounded-2xl rounded-bl-md border border-slate-100 shadow-sm'
                  }`}
                >
                  {m.text ? renderText(m.text) : (
                    <span className="inline-flex gap-1 items-center py-1">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce [animation-delay:120ms]" />
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce [animation-delay:240ms]" />
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="p-3 bg-white border-t border-slate-100 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Scrivi un messaggio..."
              className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#0288D1] transition-colors"
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={streaming || !input.trim()}
              className="w-10 h-10 shrink-0 bg-[#FF7043] text-white rounded-full flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      <button
        data-testid="chat-bubble-btn"
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-5 right-4 md:right-6 z-[110] w-14 h-14 bg-[#0288D1] hover:bg-[#0277BD] text-white rounded-full shadow-xl shadow-blue-400/30 flex items-center justify-center transition-all hover:scale-110"
        aria-label="Apri chat assistente"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
        {!open && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#FF7043] rounded-full border-2 border-white" />}
      </button>
    </>
  );
}
